import { computed, nextTick, onMounted, reactive, ref, type Ref, watch } from 'vue'
import { onClickOutside, useElementSize } from '@vueuse/core'
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { useVisibleChipReorder } from './useVisibleChipReorder'

interface VueComponentRef {
  $el: HTMLElement
}

interface FocusableRef {
  focus: () => void
}

type MaybeElementRef = VueComponentRef | HTMLElement | null | undefined

type NamedFilterSet = {
  id: string
  name: string
}

const CONTAINER_WIDTH_BUFFER = 16
const FALLBACK_CHIP_WIDTH = 155

const TRUNCATION = {
  CHIP_NAME_LENGTH: 25,
  MENU_NAME_LENGTH: 40,
} as const

export function useJobFilterSetsUi<T extends NamedFilterSet>(options: {
  orderedFilterSets: Ref<T[]>
  nameInputElement: Ref<FocusableRef | null>
  updateNameInputElement: Ref<FocusableRef | null>
  isFetching: Ref<boolean>
  isCreating: Ref<boolean>
  isUpdating: Ref<boolean>
  isDeleting: Ref<boolean>
  loadFilterSet: (filterSet: T) => void
  reorderVisibleFilterSets: (ids: string[]) => void
}) {
  const saveButtonRef = ref<VueComponentRef | null>(null)
  const allJobsChipRef = ref<VueComponentRef | null>(null)
  const moreChipRef = ref<MaybeElementRef>(null)
  const moreChipMeasureRef = ref<MaybeElementRef>(null)
  const containerRef = ref<HTMLElement | null>(null)
  const contentAreaRef = ref<HTMLElement | null>(null)
  const updateMenuRef = ref<HTMLElement | null>(null)
  const updateMenuReference = ref<HTMLElement | null>(null)

  const visibleChipRefs = reactive<Record<string, HTMLElement | null>>({})
  const measuredChipRefs = reactive<Record<string, HTMLElement | null>>({})
  const chipWidths = ref<Record<string, number>>({})
  const layoutVersion = ref(0)
  const { width: containerWidth } = useElementSize(containerRef)

  const ui = reactive({
    isSaveMenuVisible: false,
    isUpdateMenuVisible: false,
    isMoreSetsMenuVisible: false,
    newFilterSetName: '',
    updateFilterSetName: '',
    updateFilterSetId: null as string | null,
    openMenuId: null as string | null,
    validationError: null as string | null,
    updateValidationError: null as string | null,
  })

  const toEl = (maybe: MaybeElementRef): HTMLElement | null => {
    if (!maybe) {
      return null
    }

    return maybe instanceof HTMLElement ? maybe : maybe.$el
  }

  const getOuterWidth = (element: HTMLElement | null) => {
    if (!element) {
      return 0
    }

    const styles = window.getComputedStyle(element)
    const marginLeft = parseFloat(styles.marginLeft || '0')
    const marginRight = parseFloat(styles.marginRight || '0')

    return element.getBoundingClientRect().width + marginLeft + marginRight
  }

  const setVisibleChipRef = (id: string, maybe: MaybeElementRef) => {
    visibleChipRefs[id] = toEl(maybe)
  }

  const setMeasuredChipRef = (id: string, maybe: MaybeElementRef) => {
    measuredChipRefs[id] = toEl(maybe)
  }

  const maxOverflowChipLabel = computed(
    // Reserve space using the largest possible "+N more" label. The real
    // hidden count depends on this width, so sizing from the current hidden
    // count would create a circular dependency in the overflow calculation.
    () => `+${options.orderedFilterSets.value.length} more...`,
  )

  const updateMenuOpen = computed(() => ui.isUpdateMenuVisible)
  const { floatingStyles } = useFloating(updateMenuReference, updateMenuRef, {
    strategy: 'fixed',
    open: updateMenuOpen,
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  })
  const cachedUpdateMenuStyles = ref<Record<string, string | number>>({})
  const updateMenuStyles = computed(() =>
    ui.isUpdateMenuVisible ? floatingStyles.value : cachedUpdateMenuStyles.value,
  )

  const truncateName = (name: string, maxLength: number) =>
    name.length >= maxLength ? `${name.substring(0, maxLength - 3)}...` : name

  const shouldShowTooltip = (name: string, maxLength: number) => name.length >= maxLength

  const closeMoreMenu = () => {
    ui.isMoreSetsMenuVisible = false
  }

  const closeUpdateMenu = () => {
    ui.isUpdateMenuVisible = false
    ui.updateValidationError = null
  }

  const resetSaveMenu = () => {
    ui.newFilterSetName = ''
    ui.validationError = null
    ui.openMenuId = null
    ui.isMoreSetsMenuVisible = false
    ui.isSaveMenuVisible = false
  }

  const resetUpdateMenu = () => {
    ui.updateFilterSetName = ''
    ui.updateFilterSetId = null
    ui.updateValidationError = null
    closeUpdateMenu()
  }

  const setValidationError = (error: string | null) => {
    ui.validationError = error
  }

  const setUpdateValidationError = (error: string | null) => {
    ui.updateValidationError = error
  }

  const setNewFilterSetName = (name: string) => {
    ui.newFilterSetName = name
  }

  const setUpdateFilterSetName = (name: string) => {
    ui.updateFilterSetName = name
  }

  const toggleMenu = (filterSetId: string) => {
    ui.openMenuId = ui.openMenuId === filterSetId ? null : filterSetId
  }

  const syncMenuVisibility = (filterSetId: string, isVisible: boolean) => {
    if (isVisible) {
      ui.openMenuId = filterSetId
      return
    }

    if (ui.openMenuId === filterSetId) {
      ui.openMenuId = null
    }
  }

  const openUpdateMenu = (filterSet: T, fromOverflow = false) => {
    updateMenuReference.value = fromOverflow
      ? toEl(moreChipRef.value)
      : (visibleChipRefs[filterSet.id] ?? contentAreaRef.value)

    ui.updateFilterSetId = filterSet.id
    ui.updateFilterSetName = filterSet.name
    ui.updateValidationError = null
    ui.isUpdateMenuVisible = true
    ui.isMoreSetsMenuVisible = false
    ui.openMenuId = null

    nextTick(() => options.updateNameInputElement.value?.focus())
  }

  const openUpdateMenuFromOverflow = (filterSet: T) => {
    openUpdateMenu(filterSet, true)
  }

  const measureChipWidths = () => {
    requestAnimationFrame(() => {
      const newWidths = options.orderedFilterSets.value.reduce(
        (acc, filterSet) => {
          acc[filterSet.id] =
            getOuterWidth(measuredChipRefs[filterSet.id]) ||
            chipWidths.value[filterSet.id] ||
            FALLBACK_CHIP_WIDTH
          return acc
        },
        {} as Record<string, number>,
      )

      const unchanged =
        Object.keys(newWidths).length === Object.keys(chipWidths.value).length &&
        Object.entries(newWidths).every(
          ([id, width]) => Math.abs((chipWidths.value[id] ?? 0) - width) < 1,
        )

      if (!unchanged) {
        chipWidths.value = newWidths
      }
    })
  }

  const refreshLayout = () => {
    layoutVersion.value += 1
  }

  const chipVisibility = computed(() => {
    const totalChips = options.orderedFilterSets.value.length

    if (totalChips === 0 || !containerWidth.value) {
      return { visible: totalChips, showMore: false }
    }

    const saveButtonWidth = getOuterWidth(toEl(saveButtonRef.value))
    const allJobsChipWidth = getOuterWidth(toEl(allJobsChipRef.value))
    const moreChipWidth = getOuterWidth(toEl(moreChipMeasureRef.value))

    // layoutVersion forces one extra post-render recalculation when chip order
    // changes so this read reflects the latest arrangement of visible chips.
    const contentWidth =
      layoutVersion.value >= 0
        ? contentAreaRef.value?.getBoundingClientRect().width || containerWidth.value
        : containerWidth.value

    const availableWidth =
      contentWidth - saveButtonWidth - allJobsChipWidth - CONTAINER_WIDTH_BUFFER

    let usedWidth = 0
    let visibleCount = 0

    for (let i = 0; i < totalChips; i++) {
      const chipWidth =
        chipWidths.value[options.orderedFilterSets.value[i].id] || FALLBACK_CHIP_WIDTH
      const isNotLastChip = i < totalChips - 1
      const reservedWidth = isNotLastChip ? moreChipWidth : 0

      if (usedWidth + chipWidth + reservedWidth <= availableWidth) {
        usedWidth += chipWidth
        visibleCount++
      } else {
        break
      }
    }

    return {
      visible: visibleCount,
      showMore: visibleCount < totalChips,
    }
  })

  const visibleFilterSets = computed(() =>
    options.orderedFilterSets.value.slice(0, chipVisibility.value.visible),
  )

  const hiddenFilterSets = computed(() =>
    options.orderedFilterSets.value.slice(chipVisibility.value.visible),
  )

  const canDragVisibleFilterSetsBase = computed(
    () =>
      !ui.isSaveMenuVisible &&
      !ui.isUpdateMenuVisible &&
      !ui.isMoreSetsMenuVisible &&
      !ui.openMenuId &&
      !options.isFetching.value &&
      !options.isCreating.value &&
      !options.isUpdating.value &&
      !options.isDeleting.value,
  )

  const {
    visibleItemModel: visibleFilterSetModel,
    isDragging: isDraggingVisibleFilterSets,
    suppressHover: suppressChipHover,
    canDragVisibleItems: canDragVisibleFilterSets,
    handleDragStart: handleVisibleFilterSetDragStart,
    handleDragChange: handleVisibleFilterSetDragChange,
    handleDragEnd: handleVisibleFilterSetDragEnd,
    handlePointerMove: handleChipPointerMove,
    handlePointerLeave: handleChipPointerLeave,
    handleItemClick: handleFilterSetClick,
  } = useVisibleChipReorder({
    visibleItems: visibleFilterSets,
    canDrag: canDragVisibleFilterSetsBase,
    onReorder: options.reorderVisibleFilterSets,
    onClickItem: options.loadFilterSet,
  })

  onClickOutside(updateMenuRef, () => {
    if (ui.isUpdateMenuVisible) {
      closeUpdateMenu()
    }
  })

  watch(
    options.orderedFilterSets,
    () => {
      nextTick(() => {
        measureChipWidths()
        refreshLayout()
      })
    },
    {
      deep: true,
      flush: 'post',
    },
  )

  watch(
    containerWidth,
    () => {
      nextTick(() => {
        measureChipWidths()
        refreshLayout()
      })
    },
    { flush: 'post' },
  )

  watch(
    () => ui.isSaveMenuVisible,
    (isVisible) => {
      if (isVisible) {
        ui.validationError = null
        nextTick(() => options.nameInputElement.value?.focus())
      }
    },
  )

  watch(
    () => ui.isUpdateMenuVisible,
    (isVisible) => {
      if (isVisible) {
        ui.updateValidationError = null
      }
    },
  )

  watch(
    floatingStyles,
    (styles) => {
      if (!ui.isUpdateMenuVisible || !styles || Object.keys(styles).length === 0) {
        return
      }

      cachedUpdateMenuStyles.value = { ...styles }
    },
    { deep: true },
  )

  watch(
    () => ui.newFilterSetName,
    () => {
      if (ui.validationError) {
        ui.validationError = null
      }
    },
  )

  watch(
    () => ui.updateFilterSetName,
    () => {
      if (ui.updateValidationError) {
        ui.updateValidationError = null
      }
    },
  )

  watch(
    () => ui.isMoreSetsMenuVisible,
    () => {
      if (ui.openMenuId) {
        ui.openMenuId = null
      }
    },
  )

  onMounted(async () => {
    await nextTick()
    measureChipWidths()
    refreshLayout()
  })

  const elements = {
    containerRef,
    contentAreaRef,
    saveButtonRef,
    allJobsChipRef,
    moreChipRef,
    moreChipMeasureRef,
    updateMenuRef,
  }

  return {
    TRUNCATION,
    ui,
    elements,
    updateMenuStyles,
    chipVisibility,
    visibleFilterSets,
    hiddenFilterSets,
    visibleFilterSetModel,
    isDraggingVisibleFilterSets,
    suppressChipHover,
    canDragVisibleFilterSets,
    maxOverflowChipLabel,
    setVisibleChipRef,
    setMeasuredChipRef,
    truncateName,
    shouldShowTooltip,
    toggleMenu,
    syncMenuVisibility,
    handleVisibleFilterSetDragStart,
    handleVisibleFilterSetDragChange,
    handleVisibleFilterSetDragEnd,
    handleChipPointerMove,
    handleChipPointerLeave,
    handleFilterSetClick,
    openUpdateMenu,
    openUpdateMenuFromOverflow,
    closeMoreMenu,
    closeUpdateMenu,
    resetSaveMenu,
    resetUpdateMenu,
    setValidationError,
    setUpdateValidationError,
    setNewFilterSetName,
    setUpdateFilterSetName,
  }
}
